import React, { useState } from 'react';
import * as yup from 'yup';
import { each } from 'lodash';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { HiOutlineMail } from 'react-icons/hi';

import FormInput from 'partial/form/FormInput';
import Button from 'partial/components/Button';
import { ModalCard } from 'partial/components/Modal';
import Form, { ErrorLabel } from 'partial/form/Form';
import VerifyEmailIcon from 'assets/images/icons/icon-verify-email.svg';

import { useForgotPassword } from '../hooks';

const validationSchema = yup.object({
  email_address: yup
    .string()
    .email('Please enter a valid email address.')
    .required('Required.'),
});

function ForgotPasswordModal({ onClose }) {
  const [willValidateForm, setWillValidateForm] = useState(false);
  const [isSentToEmail, setIsSentToEmail] = useState(false);

  const {
    isLoadingSendEmail: isLoading,
    sendEmailInstruction,
  } = useForgotPassword({
    onSendInstructions: () => setIsSentToEmail(true),
  });

  const {
    values,
    errors,
    handleSubmit,
    setFieldValue,
    setFieldError,
  } = useFormik({
    validationSchema,
    validateOnChange: willValidateForm,
    initialValues: {
      email_address: '',
    },
    onSubmit: (form) =>
      sendEmailInstruction(form, null, (err) => {
        each(err?.response?.errors, (v, k) => {
          setFieldError(k, v);
        });
      }),
  });

  return (
    <ModalCard title="Forgot Password" onClose={onClose} size="sm">
      {isSentToEmail ? (
        <div className="w-full pb-6 px-6 flex flex-col items-center">
          <img src={VerifyEmailIcon} alt="" className="w-36 py-3" />
          <h6 className="text-xl font-semibold">Verify Your email</h6>
          <p className="mt-6 text-center">
            We have sent an email verification link to{' '}
            <span className="font-semibold text-gray-600">
              {values?.email_address || '--'}
            </span>{' '}
            to verify your account.
          </p>
          <div className="mt-6">
            <Button outlinePrimary onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      ) : (
        <Form
          className="w-full p-6 space-y-10"
          onSubmit={handleSubmit}
          error={errors}
        >
          <p className="text-gray-500 text-lg">
            Enter your registered email below to receive password reset
            instructions.
          </p>
          <div>
            <FormInput
              name="email_address"
              label="Email address"
              placeholder="Enter your email address"
              value={values.email_address}
              onSetFieldValue={setFieldValue}
              icon={HiOutlineMail}
              required
            />
            <ErrorLabel name="email_address" />
          </div>
          <div className="flex gap-2 text-sm justify-center">
            <Button
              className="w-2/5"
              outlinePrimary
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              className="w-2/5"
              primary
              type="submit"
              disabled={isLoading}
              onClick={() => setWillValidateForm(true)}
            >
              Send Instructions
            </Button>
          </div>
        </Form>
      )}
    </ModalCard>
  );
}

ForgotPasswordModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default ForgotPasswordModal;
